import Raven from 'raven-js';
import { useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import styled from 'foundations-theming/utils';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import UsageTracker from '../../../utils/UsageTracker';
import { useStartExam } from '../examsQueries';
import { useExamRulesState } from '../examsStores';
import { BaseContentControlWrapper } from '../../../components/ContentControlWrapper';
import { ContactUsLink } from '../../../components/ContactUsLink';
import { calculateExamDurationSeconds } from '../../../utils/ContentUtils';
import { getHumanizedDuration } from '../../../utils/utils';
import { Metrics } from '../../../Metrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// @bend-expect trellis-tokenized
const PaddedContainer = styled.div.withConfig({
  displayName: "ExamRulesScreen__PaddedContainer"
})(["padding:10% 5%;"]);
const ExamRulesScreen = ({
  cooldownHourCount = 12,
  trackId,
  track,
  onExamStart
}) => {
  var _track$exam, _track$exam2, _track$exam3;
  const {
    isDesktop
  } = useMediaQuery();
  const {
    setShowExamRules
  } = useExamRulesState();
  const [startExam, {
    startExamLoading,
    startExamError
  }] = useStartExam();
  const requiredCorrectQuestionCount = (track === null || track === void 0 || (_track$exam = track.exam) === null || _track$exam === void 0 ? void 0 : _track$exam.passThreshold) || 0;
  const questionCount = (track === null || track === void 0 || (_track$exam2 = track.exam) === null || _track$exam2 === void 0 ? void 0 : _track$exam2.length) || 0;
  const durationMinutes = (track === null || track === void 0 || (_track$exam3 = track.exam) === null || _track$exam3 === void 0 ? void 0 : _track$exam3.durationMinutes) || 0;
  const handleStart = useCallback(() => {
    if (!track || !track.exam) {
      return null;
    }
    return startExam(Number(trackId), track.exam.id, track.exam.language).then(attempt => {
      var _track$exam4, _attempt$questions, _attempt$id, _track$exam5, _track$exam6;
      setShowExamRules(false);
      onExamStart === null || onExamStart === void 0 || onExamStart();
      UsageTracker.trackStandalone('Exam Interaction', {
        action: 'start',
        examId: track === null || track === void 0 || (_track$exam4 = track.exam) === null || _track$exam4 === void 0 ? void 0 : _track$exam4.id,
        examLength: attempt === null || attempt === void 0 || (_attempt$questions = attempt.questions) === null || _attempt$questions === void 0 ? void 0 : _attempt$questions.length,
        trackId: trackId === null || trackId === void 0 ? void 0 : trackId.toString(),
        trackName: track.name,
        examAttemptId: attempt === null || attempt === void 0 || (_attempt$id = attempt.id) === null || _attempt$id === void 0 ? void 0 : _attempt$id.toString(),
        language: (_track$exam5 = track.exam) === null || _track$exam5 === void 0 ? void 0 : _track$exam5.language
      });
      if ((_track$exam6 = track.exam) !== null && _track$exam6 !== void 0 && _track$exam6.language) {
        var _track$exam7;
        Metrics.counter('exam-started', {
          language: (_track$exam7 = track.exam) === null || _track$exam7 === void 0 ? void 0 : _track$exam7.language
        }).increment();
      }
    }).catch(error => {
      Metrics.counter('exam-mutation-call-error', {
        action: 'exam-start-error'
      }).increment();
      Raven.captureException(error instanceof Error ? error : new Error('Start Exam Error'), {
        extra: {
          originalError: error
        }
      });
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "learningCenter.exams.rules.startExamError",
          options: {
            contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
          }
        })
      });
    });
  }, [onExamStart, setShowExamRules, startExam, track, trackId]);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(View, {
      backgroundColor: "fill-surface-default",
      children: /*#__PURE__*/_jsx(PaddedContainer, {
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          justifyContent: "center",
          children: [isDesktop && /*#__PURE__*/_jsx(Flex, {
            alignSelf: "start",
            marginBlockStart: "500",
            marginInlineEnd: "800",
            children: /*#__PURE__*/_jsx(Illustration, {
              name: "nps-question",
              width: 150
            })
          }), /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(H4, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.exams.rules.heading"
              })
            }), /*#__PURE__*/_jsxs("div", {
              children: [/*#__PURE__*/_jsxs(List, {
                styled: true,
                children: [/*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.questionCount",
                    options: {
                      questionCount
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.duration",
                    options: {
                      expectedDuration: getHumanizedDuration(calculateExamDurationSeconds(questionCount))
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.requiredCorrectQuestionsCount",
                    options: {
                      requiredCorrectQuestionCount
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.cooldownHourDurationCount",
                    options: {
                      cooldownHourCount
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.maximumDuration",
                    options: {
                      maximumDuration: getHumanizedDuration(durationMinutes, 'minutes')
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.warningNoBackNavigation"
                  })
                })]
              }), /*#__PURE__*/_jsx("span", {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.exams.rules.footer"
                })
              })]
            })]
          })]
        })
      })
    }), /*#__PURE__*/_jsx(BaseContentControlWrapper, {
      borderStyle: "none",
      children: /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        justifyContent: "end",
        children: /*#__PURE__*/_jsx(LoadingButton, {
          "data-test-id": "exam-start-button",
          failed: !!startExamError,
          loading: startExamLoading,
          onClick: () => {
            void handleStart();
          },
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.exams.rules.startExam"
          })
        })
      })
    })]
  });
};
export default ExamRulesScreen;